import { useState, useEffect } from 'react';
import { 
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  MapPinIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { usersAPI } from '../../lib/api';
import { useRoles } from '../../hooks/useRoles';
import { useDesignations } from '../../hooks/useSharedData';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

const UserModal = ({ user, onClose, onSave, roles, rolesLoading, companies }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    role: '', 
    company_id: '',
    designation: '', // Keep for backward compatibility
    designation_id: '', // New designation_id field
    dgp_id: '', // DGP ID field
    badge_no: '', // Badge number
    active: true,
    whatsapp_enabled: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [viewMode, setViewMode] = useState(false);

  
  // Fetch designations from API
  const { data: designationsResponse, isLoading: designationsLoading, error: designationsError } = useDesignations();
  const designations = designationsResponse?.data?.designations || [];
  
  
  // Debug: Log designations data
  console.log('💼 Designations Response:', designationsResponse);
  console.log('💼 Designations Array:', designations);
  console.log('💼 Designations Loading:', designationsLoading);
  console.log('💼 Designations Error:', designationsError);
  

  // Update form data when user prop changes
  useEffect(() => {
    if (user) {
      // Editing existing user
             setFormData({
         first_name: user.first_name || '',
         middle_name: user.middle_name || '',
         last_name: user.last_name || '',
         email: user.email || '',
         phone_number: formatPhoneForDisplay(user.phone_number) || '',
         password: '', // Clear password for existing users
         designation: user.designation || '', // Keep for backward compatibility
         designation_id: user.designation_id?.toString() || '', // New designation_id field
         dgp_id: user.dgp_id || '', // DGP ID field
         badge_no: user.badge_no?.toString() || '', // Badge number
        role: user.role_name || 'viewer',
        company_id: user.company_id?.toString() || '',
        active: user.active ?? true,
        whatsapp_enabled: user.whatsapp_enabled ?? true
       });
      setViewMode(user.viewMode || false);
    } else {
      // Creating new user - reset all fields
             setFormData({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        password: '',
        role: '',
        company_id: '',
        designation: '', // Keep for backward compatibility
        designation_id: '', // New designation_id field
        dgp_id: '', // DGP ID field
        badge_no: '', // Badge number
        active: true,
        whatsapp_enabled: true
      });
      setViewMode(false);
    }
    setErrors({});
  }, [user]);

  // Reset form when modal opens for new user
  const resetForm = () => {
     setFormData({
       first_name: '',
       middle_name: '',
       last_name: '',
       email: '',
       phone_number: '',
       password: '',
       role: '', // Default role for new users
       company_id: '',
       designation: '', // Keep for backward compatibility
       designation_id: '', // New designation_id field
       dgp_id: '', // DGP ID field
       badge_no: '', // Badge number
      active: true,   // Default to active for new users
      whatsapp_enabled: true
     });
     setErrors({});
     setViewMode(false);
   };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate required fields before sending
      if (!formData.first_name?.trim()) {
        setErrors({ first_name: 'First name is required' });
        setLoading(false);
        return;
      }
      if (!formData.middle_name?.trim()) {
        setErrors({ middle_name: 'Middle name is required' });
        setLoading(false);
        return;
      }
      if (!formData.last_name?.trim()) {
        setErrors({ last_name: 'Last name is required' });
        setLoading(false);
        return;
      }
      // Validate email format only if email is provided (email is now optional)
      if (formData.email?.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          setErrors({ email: 'Please enter a valid email address' });
          setLoading(false);
          return;
        }
      }
      
      // Validate phone number - must be exactly 10 digits
      if (formData.phone_number && formData.phone_number.trim()) {
        const phoneDigits = formData.phone_number.replace(/\D/g, ''); // Remove all non-digits
        if (phoneDigits.length !== 10) {
          alert('❌ Phone number must be exactly 10 digits');
          setErrors({ phone_number: 'Phone number must be exactly 10 digits' });
          setLoading(false);
          return;
        }
      }
      
      if (!formData.role?.trim()) {
        setErrors({ role: 'Role is required' });
        setLoading(false);
        return;
      }
      if (!formData.company_id) {
        setErrors({ company_id: 'Company ID is required' });
        setLoading(false);
        return;
      }
      if (!user && !formData.password?.trim()) {
        setErrors({ password: 'Password is required for new users' });
        setLoading(false);
        return;
      }

      // Prepare data for API - match your backend schema exactly
      const apiData = {
        first_name: formData.first_name.trim(),
        middle_name: formData.middle_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email?.trim() || null, // Email is now optional
        phone: formData.phone_number?.trim() || null, // Backend expects 'phone', not 'phone_number'
        password: formData.password?.trim() || null, // Backend expects 'password'
        role: formData.role, // Backend expects 'role' as string
        company_id: parseInt(formData.company_id),
        designation: formData.designation?.trim() || null, // Keep for backward compatibility
        designation_id: formData.designation_id ? parseInt(formData.designation_id) : null, // New designation_id field
        dgp_id: formData.dgp_id?.trim() || null, // DGP ID field
        badge_no: formData.badge_no ? parseInt(formData.badge_no) : null, // Badge number
      is_active: formData.active, // Backend expects 'is_active', not 'active'
      whatsapp_enabled: formData.whatsapp_enabled
      };

      console.log('📤 Sending data to API:', apiData);
      console.log('🔍 Form data before submission:', formData);
             console.log('🔍 Backend schema expects:', {
         email: 'string (required)',
         password: 'string min 8 chars (required)',
         first_name: 'string min 2 chars (required)',
         last_name: 'string min 2 chars (required)',
         role: 'admin|executive|company_lead|officer|viewer (required)',
         company_id: 'number (required)',
         phone: 'string (optional)',
         is_active: 'boolean (optional, default true)'
       });
      console.log('🔍 Email validation check:', {
        email: formData.email,
        trimmed: formData.email?.trim(),
        isValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email?.trim() || '')
      });

      if (user) {
        // Update existing user
        console.log('🔄 Updating user with ID:', user.id);
        const response = await usersAPI.update(user.id, apiData);
        console.log('✅ Update response:', response);
        alert('✅ User updated successfully!');
      } else {
        // Create new user
        console.log('➕ Creating new user');
        console.log('📡 API endpoint: POST /users');
        const response = await usersAPI.create(apiData);
        console.log('✅ Create response:', response);
        alert('✅ User created successfully!');
      }
      
      console.log('🎉 User saved successfully!');
      onSave();
    } catch (error) {
      console.error('❌ Error saving user:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Normalize message for duplicate-field detection
      const rawMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || '';

      // Specific duplicate field alerts (email, phone, badge_no, dgp_id, icard_no)
      if (/email already exists/i.test(rawMessage)) {
        setErrors({ email: rawMessage });
        alert(`❌ ${rawMessage}`);
        setLoading(false);
        return;
      }
      if (/phone already exists/i.test(rawMessage)) {
        setErrors({ phone_number: rawMessage });
        alert(`❌ ${rawMessage}`);
        setLoading(false);
        return;
      }
      if (/badge(\s)?(no|number) already exists/i.test(rawMessage)) {
        setErrors({ badge_no: rawMessage });
        alert(`❌ ${rawMessage}`);
        setLoading(false);
        return;
      }
      if (/dgp(\s)?id already exists/i.test(rawMessage)) {
        setErrors({ dgp_id: rawMessage });
        alert(`❌ ${rawMessage}`);
        setLoading(false);
        return;
      }
      if (/icard(\s)?(no|number) already exists/i.test(rawMessage)) {
        setErrors({ icard_no: rawMessage });
        alert(`❌ ${rawMessage}`);
        setLoading(false);
        return;
      }

      // Handle different error response formats
      if (error.response?.data?.error?.details) {
        // Handle field-specific validation errors
        setErrors(error.response.data.error.details);
        const errorMessages = Object.values(error.response.data.error.details);
        const errorMsg = `Validation Errors: ${errorMessages.join(', ')}`;
        alert(`❌ ${errorMsg}`);
      } else if (error.response?.data?.error?.field) {
        // Handle single field error (like email already exists)
        const field = error.response.data.error.field;
        const message = error.response.data.error.message;
        setErrors({ [field]: message });
        alert(`❌ ${message}`);
      } else if (error.response?.data?.error?.message) {
        // Handle general error message
        setErrors({ general: error.response.data.error.message });
        alert(`❌ ${error.response.data.error.message}`);
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
        alert(`❌ ${error.response.data.message}`);
      } else if (error.response?.status === 400) {
        const errorMsg = 'Invalid data. Please check your input and try again.';
        setErrors({ general: errorMsg });
        alert(`❌ ${errorMsg}`);
      } else if (error.response?.status === 401) {
        const errorMsg = 'Authentication required. Please login again.';
        setErrors({ general: errorMsg });
        alert(`⚠️ ${errorMsg}`);
      } else if (error.response?.status === 403) {
        const errorMsg = 'Permission denied. You cannot create users.';
        setErrors({ general: errorMsg });
        alert(`❌ ${errorMsg}`);
      } else if (error.response?.status === 409) {
        const errorMsg = 'User with this email already exists.';
        setErrors({ general: errorMsg });
        alert(`❌ ${errorMsg}`);
      } else if (error.response?.status >= 500) {
        const errorMsg = 'Server error. Please try again later.';
        setErrors({ general: errorMsg });
        alert(`❌ ${errorMsg}`);
      } else {
        const errorMsg = `Failed to save user: ${error.message || 'Unknown error'}`;
        setErrors({ general: errorMsg });
        alert(`❌ ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear existing errors for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
    
    // Real-time phone number validation
    if (field === 'phone_number' && value && value.trim()) {
      const phoneDigits = value.replace(/\D/g, ''); // Remove all non-digits
      if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
        setErrors(prev => ({ 
          ...prev, 
          phone_number: `Phone number must be exactly 10 digits (${phoneDigits.length} digits entered)` 
        }));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {viewMode ? 'View User' : (user ? 'Edit User' : 'Add New User')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* General Error Display */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                autoComplete="off"
                required
                disabled={viewMode}
              />
              {errors.first_name && (
                <p className="text-sm text-red-600 mt-1">{errors.first_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Middle Name
              </label>
              <input
                type="text"
                value={formData.middle_name}
                onChange={(e) => handleInputChange('middle_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                placeholder="Enter middle name"
                autoComplete="off"
                required
                disabled={viewMode}
              />
              {errors.middle_name && (
                <p className="text-sm text-red-600 mt-1">{errors.middle_name}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                autoComplete="off"
                required
                disabled={viewMode}
              />
              {errors.last_name && (
                <p className="text-sm text-red-600 mt-1">{errors.last_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                autoComplete="off"
                placeholder="Enter email (optional)"
                disabled={viewMode}
              />
              {errors.email && (
                <p className="text-sm text-red-600 mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                placeholder="Enter 10-digit phone number"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                autoComplete="off"
                disabled={viewMode}
              />
              {errors.phone_number && (
                <p className="text-sm text-red-600 mt-1">{errors.phone_number}</p>
              )}
            </div>

            {/* Only show password field for new users */}
            {!user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                  autoComplete="new-password"
                  required={!user} // Only required for new users
                  disabled={viewMode}
                />
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                required
                disabled={viewMode || rolesLoading}
              >
                {rolesLoading ? (
                  <option value="">Loading roles...</option>
                ) : (
                  <>
                    <option value="">Select Role</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.name}>
                        {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ')}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {errors.role && (
                <p className="text-sm text-red-600 mt-1">{errors.role}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company
              </label>
              <select
                value={formData.company_id}
                onChange={(e) => handleInputChange('company_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                required
                disabled={viewMode}
              >
                <option value="">Select Company</option>
                {companies?.data?.data?.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {errors.company_id && (
                <p className="text-sm text-red-600 mt-1">{errors.company_id}</p>
              )}
            </div>
          </div>

          {/* Designation */}
          <div className="grid grid-cols-1 gap-4">

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Designation
              </label>
              <select
                value={formData.designation_id}
                onChange={(e) => handleInputChange('designation_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                disabled={viewMode || designationsLoading}
              >
                <option value="">Select Designation</option>
                {designationsLoading ? (
                  <option value="">Loading designations...</option>
                ) : (
                  designations.map(designation => (
                    <option key={designation.id} value={designation.id}>
                      {designation.name}
                    </option>
                  ))
                )}
              </select>
              {errors.designation_id && (
                <p className="text-sm text-red-600 mt-1">{errors.designation_id}</p>
              )}
            </div>
          </div>

          {/* DGP ID */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                DGP ID
              </label>
              <input
                type="text"
                value={formData.dgp_id}
                onChange={(e) => handleInputChange('dgp_id', e.target.value.toUpperCase())}
                placeholder="e.g., DGPSBLM7304"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                autoComplete="off"
                disabled={viewMode}
                maxLength={11}
              />
              {errors.dgp_id && (
                <p className="text-sm text-red-600 mt-1">{errors.dgp_id}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Format: 7 uppercase letters followed by 4 digits (e.g., DGPSBLM7304)
              </p>
            </div>
          </div>

          {/* Badge Number */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Badge Number
              </label>
              <input
                type="number"
                value={formData.badge_no}
                onChange={(e) => handleInputChange('badge_no', e.target.value)}
                placeholder="e.g., 12345"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                autoComplete="off"
                disabled={viewMode}
                min="1"
              />
              {errors.badge_no && (
                <p className="text-sm text-red-600 mt-1">{errors.badge_no}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => handleInputChange('active', e.target.checked)}
              className="rounded border-gray-300 text-brand-primary focus:ring-2 focus:ring-brand-accent disabled:cursor-not-allowed"
              disabled={viewMode}
            />
            <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              User is active
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="whatsapp_enabled"
              checked={formData.whatsapp_enabled}
              onChange={(e) => handleInputChange('whatsapp_enabled', e.target.checked)}
              className="rounded border-gray-300 text-brand-primary focus:ring-2 focus:ring-brand-accent disabled:cursor-not-allowed"
              disabled={viewMode}
            />
            <label htmlFor="whatsapp_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              WhatsApp enabled
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="px-4 py-2 text-sm"
            >
              Cancel
            </Button>
            {!viewMode && (
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="px-4 py-2 text-sm"
              >
                {loading ? 'Saving...' : (user ? 'Update User' : 'Create User')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
export default UserModal;